import { cn } from '@/lib/utils'

/**
 * Sales' three ratings of a manufacturer in a store category (USA-1258, USA-1272), with one color
 * per value so a row reads at a glance. Field names match the API: `quality_tier`, `preference`,
 * `pricing`. The label always shows; color only reinforces it.
 */
export type ManufacturerRatingKind = 'quality_tier' | 'preference' | 'pricing'

export type ManufacturerRatingValue = {
  quality_tier: 'good' | 'better' | 'best'
  preference: 'not_preferred' | 'neutral' | 'preferred'
  pricing: 'budget' | 'neutral' | 'expensive'
}

type Option<K extends ManufacturerRatingKind> = {
  value: ManufacturerRatingValue[K]
  label: string
  /** Tinted chip: fill, text and hairline ring, light and dark. */
  tone: string
}

const EMERALD =
  'bg-emerald-50 text-emerald-800 ring-emerald-600/25 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/30'
const NEUTRAL =
  'bg-zinc-100 text-zinc-700 ring-zinc-500/25 dark:bg-zinc-500/15 dark:text-zinc-300 dark:ring-zinc-400/30'

export const MANUFACTURER_RATINGS: {
  [K in ManufacturerRatingKind]: { label: string; options: readonly Option<K>[] }
} = {
  quality_tier: {
    label: 'Quality',
    options: [
      {
        value: 'good',
        label: 'Good',
        tone: 'bg-amber-50 text-amber-800 ring-amber-600/30 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/30',
      },
      {
        value: 'better',
        label: 'Better',
        tone: 'bg-sky-50 text-sky-800 ring-sky-600/25 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-400/30',
      },
      { value: 'best', label: 'Best', tone: EMERALD },
    ],
  },
  preference: {
    label: 'Preference',
    options: [
      {
        value: 'not_preferred',
        label: 'Not preferred',
        tone: 'bg-rose-50 text-rose-800 ring-rose-600/25 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/30',
      },
      { value: 'neutral', label: 'Neutral', tone: NEUTRAL },
      { value: 'preferred', label: 'Preferred', tone: EMERALD },
    ],
  },
  pricing: {
    label: 'Pricing',
    options: [
      {
        value: 'budget',
        label: 'Budget',
        tone: 'bg-teal-50 text-teal-800 ring-teal-600/25 dark:bg-teal-500/15 dark:text-teal-300 dark:ring-teal-400/30',
      },
      { value: 'neutral', label: 'Neutral', tone: NEUTRAL },
      {
        value: 'expensive',
        label: 'Expensive',
        tone: 'bg-violet-50 text-violet-800 ring-violet-600/25 dark:bg-violet-500/15 dark:text-violet-300 dark:ring-violet-400/30',
      },
    ],
  },
}

export const MANUFACTURER_RATING_KINDS: ManufacturerRatingKind[] = ['quality_tier', 'preference', 'pricing']

function option<K extends ManufacturerRatingKind>(rating: K, value: ManufacturerRatingValue[K]) {
  return (MANUFACTURER_RATINGS[rating].options as readonly Option<K>[]).find(
    (candidate) => candidate.value === value,
  )
}

export function ratingLabel<K extends ManufacturerRatingKind>(rating: K, value: ManufacturerRatingValue[K]) {
  return option(rating, value)?.label ?? value
}

export function ratingTone<K extends ManufacturerRatingKind>(rating: K, value: ManufacturerRatingValue[K]) {
  return option(rating, value)?.tone ?? NEUTRAL
}

const CHIP = 'rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset'
// Set on a parent category: same color, dashed outline.
const INHERITED = 'opacity-80 ring-0 outline-1 -outline-offset-1 outline-dashed outline-current/40'

/** The chip's classes, for the badge and controls that look like it (an inline select's button). */
export function ratingChipClass<K extends ManufacturerRatingKind>(
  rating: K,
  value: ManufacturerRatingValue[K],
  inherited = false,
) {
  return cn(CHIP, ratingTone(rating, value), inherited && INHERITED)
}

